/**
 * KioskPage — Fullscreen tablet clock-in/out interface
 * Only accessible from registered devices (device token in localStorage).
 * PIN-based authentication per employee.
 */

import { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { KioskGrid } from '../components/kiosk/KioskGrid';
import { ClockInDialog } from '../components/kiosk/ClockInDialog';
import { DeviceBlockedScreen } from '../components/kiosk/DeviceBlockedScreen';
import { useWorkforceStore } from '../stores';

export function KioskPage() {
  const {
    kioskStaffStates,
    selectedStaffId,
    isProcessing,
    setSelectedStaff,
    clockIn,
    clockOut,
    startBreak,
    endBreak,
    startPolling,
    stopPolling,
  } = useWorkforceStore();

  const [now, setNow] = useState(new Date());
  const [deviceStatus, setDeviceStatus] = useState<'checking' | 'blocked' | 'ready'>('checking');

  // Verify device token on mount
  useEffect(() => {
    const token = localStorage.getItem('kiosk_device_token');
    if (!token) {
      setDeviceStatus('blocked');
      return;
    }
    fetch(`/api/workforce/kiosk-devices?action=verify&device_token=${token}`)
      .then(r => r.json())
      .then(data => setDeviceStatus(data.valid ? 'ready' : 'blocked'))
      .catch(() => setDeviceStatus('blocked'));
  }, []);

  const handleRegistered = useCallback(() => {
    setDeviceStatus('ready');
  }, []);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Start polling only when device is verified
  useEffect(() => {
    if (deviceStatus !== 'ready') return;
    startPolling(15000);
    return () => stopPolling();
  }, [deviceStatus, startPolling, stopPolling]);

  // Device check: loading
  if (deviceStatus === 'checking') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Device check: blocked
  if (deviceStatus === 'blocked') {
    return <DeviceBlockedScreen onRegistered={handleRegistered} />;
  }

  const selectedState = kioskStaffStates.find(s => s.staff.id === selectedStaffId);

  const clockedInCount = kioskStaffStates.filter(s => s.status !== 'out').length;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-8 py-5">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center shadow">
              <span className="text-white font-bold text-lg">C</span>
            </div>
            <span className="text-slate-800 font-semibold text-xl">Cinco Cleaning</span>
          </div>

          <div className="text-right">
            <p className="text-2xl font-semibold text-slate-800 tabular-nums">
              {format(now, 'HH:mm:ss')}
            </p>
            <p className="text-slate-500">
              {format(now, 'EEEE d MMMM yyyy', { locale: nl })}
            </p>
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <div className="bg-white border-b border-slate-100 px-8 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-6 text-sm">
          <span className="text-slate-500">
            <span className="font-semibold text-emerald-600">{clockedInCount}</span> aanwezig
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-slate-500">
            <span className="font-semibold text-slate-700">{kioskStaffStates.length}</span> medewerkers
          </span>
        </div>
      </div>

      {/* Grid */}
      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {kioskStaffStates.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 text-lg">Laden...</p>
            </div>
          ) : (
            <KioskGrid
              staffStates={kioskStaffStates}
              onSelect={setSelectedStaff}
            />
          )}
        </div>
      </main>

      {/* Dialog */}
      {selectedState && (
        <ClockInDialog
          staffState={selectedState}
          onClockIn={clockIn}
          onClockOut={clockOut}
          onBreakStart={startBreak}
          onBreakEnd={endBreak}
          onClose={() => setSelectedStaff(null)}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
}
